// @core/stores/apikey/ApikeyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { apikeyApi } from '@core/api';
import type {
  APIKey,
  APIKeyAuditLogsResponse,
  APIKeyResponse,
  APIKeyUsageHistoryResponse,
  APIKeyUsageResponse,
  ActivateAPIKeyRequest,
  CreateAPIKeyRequest,
  CreateAPIKeyResponse,
  ListPermissionsResponse,
  ListScopesResponse,
  RegenerateAPIKeyRequest,
  RevokeAPIKeyRequest,
  SuspendAPIKeyRequest,
  UpdateAPIKeyRequest,
} from '@core/types/apikey';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ApikeyStore {
  // List state
  items: APIKey[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: APIKey | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, APIKey>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): APIKey | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: APIKey | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Activate API key
   */
  async ActivateAPIKey(key_id: string, data: ActivateAPIKeyRequest): Promise<APIKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.ActivateAPIKey(key_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create API key
   */
  async CreateAPIKey(data: CreateAPIKeyRequest): Promise<APIKey> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await apikeyApi.CreateAPIKey(data);
      const item = response.api_key;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete API key
   */
  async DeleteAPIKey(key_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await apikeyApi.DeleteAPIKey(key_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== key_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === key_id) {
          this.currentItem = null;
        }

        this.removeFromCache(key_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get API key
   */
  async GetAPIKey(key_id: string): Promise<APIKey> {
    // Check cache first
    const cached = this.cache.get(key_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await apikeyApi.GetAPIKey(key_id);
      const item = response.api_key;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get API key audit logs
   */
  async GetAPIKeyAuditLogs(key_id: string): Promise<APIKeyAuditLogsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.GetAPIKeyAuditLogs(key_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get API key usage statistics
   */
  async GetAPIKeyUsage(key_id: string): Promise<APIKeyUsageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.GetAPIKeyUsage(key_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get API key usage history
   */
  async GetAPIKeyUsageHistory(key_id: string): Promise<APIKeyUsageHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.GetAPIKeyUsageHistory(key_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List API keys
   */
  async ListAPIKeys(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await apikeyApi.ListAPIKeys(params);

      runInAction(() => {
        this.items = response.api_keys?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List available permissions
   */
  async ListPermissions(params?: PaginatedRequest): Promise<ListPermissionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.ListPermissions(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List available scopes
   */
  async ListScopes(params?: PaginatedRequest): Promise<ListScopesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.ListScopes(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Regenerate API key
   */
  async RegenerateAPIKey(key_id: string, data: RegenerateAPIKeyRequest): Promise<CreateAPIKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.RegenerateAPIKey(key_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke API key
   */
  async RevokeAPIKey(key_id: string, data: RevokeAPIKeyRequest): Promise<APIKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.RevokeAPIKey(key_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Suspend API key
   */
  async SuspendAPIKey(key_id: string, data: SuspendAPIKeyRequest): Promise<APIKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await apikeyApi.SuspendAPIKey(key_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update API key
   */
  async UpdateAPIKey(key_id: string, data: UpdateAPIKeyRequest): Promise<APIKey> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await apikeyApi.UpdateAPIKey(key_id, data);
      const item = response.api_key;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const apikeyStore = new ApikeyStore();
